import { generateApps, verifyApps } from '@/mocks/playgroundData';
import BioStampCard from '@/components/feature/BioStampCard';
import { useInView } from '@/hooks/useInView';

function AppRow({
  app,
  delay,
  tone,
}: {
  app: typeof generateApps[0];
  delay: number;
  tone: 'primary' | 'accent';
}) {
  const [ref, inView] = useInView<HTMLDivElement>();
  const chip = tone === 'primary' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700';

  return (
    <div
      ref={ref}
      className={`flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70 transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${chip}`}>
        <i className={`${app.icon} text-lg`} />
      </div>
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 leading-tight">{app.name}</h4>
        <p className="text-xs text-foreground-600 leading-relaxed">{app.description}</p>
      </div>
    </div>
  );
}

export default function EcosystemDiagram() {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
      <div
        ref={ref}
        className={`text-center max-w-2xl mx-auto mb-12 transition-all duration-700 ${
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-3"
          style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
        >
          The bioMail &amp; bioVerifier Ecosystem
        </h2>
        <p className="text-base md:text-lg text-foreground-600">
          One bioStamp. Many ways to generate. Many ways to verify.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-11 gap-6 lg:gap-8 items-center max-w-[1300px] mx-auto">
        {/* Generate */}
        <div className="lg:col-span-4 rounded-2xl bg-accent-50 border border-accent-200/70 p-5">
          <h3 className="text-sm font-semibold text-accent-800 mb-4">
            Applications that generate a bioStamp
          </h3>
          <div className="space-y-3">
            {generateApps.map((app, i) => (
              <AppRow key={app.id} app={app} delay={i * 80} tone="accent" />
            ))}
          </div>
        </div>

        {/* Center hub */}
        <div className="lg:col-span-3 flex flex-col items-center">
          <div className="relative w-full max-w-[240px]">
            <span className="hidden lg:block absolute top-1/2 -left-6 w-6 h-px bg-background-300" />
            <span className="hidden lg:block absolute top-1/2 -right-6 w-6 h-px bg-background-300" />
            <div className="rounded-2xl bg-background-50 border border-background-200/70 p-4">
              <BioStampCard className="mb-3" />
              <h4 className="text-base font-bold text-foreground-950 text-center">bioStamp</h4>
              <p className="text-xs text-foreground-600 text-center leading-relaxed">
                Biometric Identity.
                <br />
                Cryptographic Proof.
              </p>
            </div>
          </div>
        </div>

        {/* Verify */}
        <div className="lg:col-span-4 rounded-2xl bg-primary-50 border border-primary-200/70 p-5">
          <h3 className="text-sm font-semibold text-primary-800 mb-4">
            Applications that verify a bioStamp
          </h3>
          <div className="space-y-3">
            {verifyApps.map((app, i) => (
              <AppRow key={app.id} app={app} delay={i * 80} tone="primary" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}